/**
 * SettingsSection Component
 * SSOT-driven section wrapper that reads from SECTION_DEFINITIONS
 * Uses CSS variables from globals.css for consistent theming
 */

import type { LucideIcon } from 'lucide-react';
import type { SectionConfig, PageKey, PageField } from '../types';
import { SECTION_DEFINITIONS, PAGE_DEFINITIONS } from '../constants';
import { FormField } from './FormField';

interface SettingsSectionProps {
    /** Page ID to look up sections */
    pageId: PageKey;
    /** Section ID within the page */
    sectionId: string;
    /** Current form values watcher */
    watch: (name: string) => any;
    /** Form value setter */
    setValue: (name: string, value: any, options?: { shouldDirty?: boolean }) => void;
    /** Whether form is currently saving */
    disabled?: boolean;
    /** Custom render for special field types (loyalty-levels, topup-tiers, etc.) */
    customFieldRenderer?: (fieldName: string, field: PageField, value: any) => React.ReactNode | null;
}

/**
 * SettingsSection - Renders a grouped section of form fields
 * Reads section config from SECTION_DEFINITIONS (SSOT)
 * Applies consistent styling using CSS variables
 */
export function SettingsSection({
    pageId,
    sectionId,
    watch,
    setValue,
    disabled = false,
    customFieldRenderer,
}: SettingsSectionProps) {
    // Look up section config from SSOT
    const sections = SECTION_DEFINITIONS[pageId];
    const section = sections?.find((s: SectionConfig) => s.id === sectionId);

    if (!section) {
        console.warn(`Section "${sectionId}" not found for page "${pageId}"`);
        return null;
    }

    // Get field definitions from PAGE_DEFINITIONS
    const pageDefinition = PAGE_DEFINITIONS.find(p => p.id === pageId);
    if (!pageDefinition) return null;

    const Icon: LucideIcon = section.icon;

    // Filter fields that belong to this section
    const sectionFields = section.fields
        .map((fieldName: string) => pageDefinition.fields.find(f => f.name === fieldName))
        .filter((field): field is PageField => field !== undefined);

    return (
        <div
            className="rounded-2xl border border-stone-200 dark:border-white/10 bg-white dark:bg-stone-900/50 overflow-hidden transition-all duration-200 hover:border-stone-300 dark:hover:border-white/20"
            style={{
                boxShadow: 'var(--shadow-sm)',
            }}
        >
            {/* Section Header */}
            <div className="px-5 py-4 border-b border-stone-100 dark:border-white/5 bg-stone-50/50 dark:bg-stone-900/30">
                <div className="flex items-center gap-3">
                    <div
                        className="flex h-9 w-9 items-center justify-center rounded-xl"
                        style={{
                            background: 'var(--accent-gradient)',
                        }}
                    >
                        <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-stone-900 dark:text-white">
                            {section.title}
                        </h3>
                        {section.description && (
                            <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                                {section.description}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Section Content - Full width layout */}
            <div className="p-5">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {sectionFields.map((field) => {
                        const fieldPath = `${pageId}.${field.name}`;
                        const value = watch(fieldPath);

                        // Check for custom renderer (for special field types)
                        if (customFieldRenderer) {
                            const customRender = customFieldRenderer(field.name, field, value);
                            if (customRender !== null) {
                                // Full width for custom renders
                                return (
                                    <div
                                        key={field.name}
                                        className="lg:col-span-2"
                                    >
                                        {customRender}
                                    </div>
                                );
                            }
                        }

                        // Determine column span based on field.className
                        const isFullWidth = field.className?.includes('col-span-2');

                        return (
                            <div
                                key={field.name}
                                className={isFullWidth ? 'lg:col-span-2' : 'lg:col-span-1'}
                            >
                                <FormField
                                    field={field}
                                    value={value}
                                    onChange={(val) => setValue(fieldPath, val, { shouldDirty: true })}
                                    disabled={disabled}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/**
 * Helper function to get all section IDs for a page
 */
export function getSectionIds(pageId: PageKey): string[] {
    const sections = SECTION_DEFINITIONS[pageId];
    return sections?.map((s: SectionConfig) => s.id) || [];
}

/**
 * Helper function to check if a page has section definitions
 */
export function hasSectionDefinitions(pageId: PageKey): boolean {
    return Boolean(SECTION_DEFINITIONS[pageId]?.length);
}

export default SettingsSection;
